import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import Modal from '../base/Modal';
import { API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';

const JiraSynchronizationModal = ({ isOpen, onClose }) => {
  const { userGoogleId } = DataState();
  const [syncData, setSyncData] = useState({ projects: [], users: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const fetchSyncData = async () => {
    if (!userGoogleId) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/auth/jira/getCounts`, {
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to get sync counts.');
      const data = await response.json();
      setSyncData(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSyncData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userGoogleId]);

  const handleAction = async (action, projectKey) => {
    const endpoint = action === 'sync' ? 'synchronize' : 'clear';
    const method = action === 'sync' ? 'POST' : 'DELETE';
    const loadingKey = `${projectKey}_${action}`;

    setActionLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const response = await fetch(
        `${API_URL}/jira_issues_and_comments/project/${endpoint}`,
        {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userGoogleId}`,
          },
          body: JSON.stringify({ project_key: projectKey }),
          credentials: 'include',
        },
      );
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || `Failed to ${action} data.`);
      }
      toast.success(`Action for project ${projectKey} initiated!`);
      await fetchSyncData(); // Refresh data
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  return (
    <Modal
      title="Jira Synchronization Status"
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="space-y-4 p-6">
        {loading ? (
          <p className="text-center text-zinc-400">Loading Status...</p>
        ) : !syncData.projects || syncData.projects.length === 0 ? (
          <p className="text-center text-zinc-400">
            No projects are selected for synchronization.
          </p>
        ) : (
          <div className="max-h-96 space-y-3 overflow-y-auto">
            {syncData.projects.map((project) => (
              <div
                key={project.key}
                className="rounded-lg border border-zinc-700 bg-zinc-800 p-3"
              >
                <p className="font-semibold text-zinc-200">
                  {project.name} ({project.key})
                </p>
                <div className="my-2 text-sm text-zinc-400">
                  <span>
                    Issues:{' '}
                    <span className="text-zinc-200">{project.issues}</span>
                  </span>{' '}
                  |
                  <span>
                    {' '}
                    Comments:{' '}
                    <span className="text-zinc-200">{project.comments}</span>
                  </span>
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    onClick={() => handleAction('clear', project.key)}
                    disabled={actionLoading[`${project.key}_clear`]}
                    className="rounded-md bg-red-800 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {actionLoading[`${project.key}_clear`]
                      ? 'Clearing...'
                      : 'Clear Data'}
                  </button>
                  <button
                    onClick={() => handleAction('sync', project.key)}
                    disabled={actionLoading[`${project.key}_sync`]}
                    className="rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-[#5652CF] disabled:opacity-50"
                  >
                    {actionLoading[`${project.key}_sync`]
                      ? 'Syncing...'
                      : 'Synchronize'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default JiraSynchronizationModal;
